import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

/**
 * 在庫評価額の明細（1 variant）。金額は PO と同様、精度維持のため文字列で受け、
 * 描画時に `Number(...)` で数値化して `yen()` で整形する。
 */
export interface ValuationLine {
  variant_id: StockyPublicBigIntId;
  product_id: StockyPublicBigIntId;
  shopify_variant_id: StockyPublicBigIntId;
  sku: string | null;
  title: string | null;
  available: number;
  unit_cost: string;
  line_value: string;
  /** 原価未設定で販売価格を暫定使用した明細は true（「目安」表示用）。 */
  is_estimated: boolean;
  /** 仕入単価がマーチャント手入力(cost_override)由来なら true。 */
  is_manual_cost: boolean;
}

export interface InventoryValuation {
  lines: ValuationLine[];
  total_value: string;
  /** いずれかの明細で原価が未設定（販売価格を暫定使用）なら true。 */
  has_estimated_lines: boolean;
}

/** ABC分析の明細（1 variant）。売上高降順で返る。 */
export interface AbcLine {
  variant_id: StockyPublicBigIntId;
  product_id: StockyPublicBigIntId;
  shopify_variant_id: StockyPublicBigIntId;
  sku: string | null;
  title: string | null;
  quantity_sold: number;
  sales_value: string;
  /** 0〜1 の累積構成比（文字列）。描画時に % へ変換する。 */
  cumulative_share: string;
  rank: "A" | "B" | "C";
}

export interface AbcAnalysis {
  lines: AbcLine[];
  total_sales_value: string;
  /**
   * 取扱SKUが少なすぎるとパレート分析が偏るため false（フロントで参考値として
   * 畳む）。false でも lines は捨てられず全件返る。
   */
  is_meaningful: boolean;
  /** is_meaningful=false の理由コード（現状 "insufficient_skus"）。true なら null。 */
  insufficient_reason: string | null;
}

export interface InventoryAnalytics {
  valuation: InventoryValuation;
  abc_analysis: AbcAnalysis;
}

/** デッドストック（滞留在庫）の明細（1 variant）。滞留資金の大きい順に返る。 */
export interface DeadStockLine {
  variant_id: StockyPublicBigIntId;
  product_id: StockyPublicBigIntId;
  shopify_variant_id: StockyPublicBigIntId;
  sku: string | null;
  title: string | null;
  available: number;
  unit_cost: string;
  /** available × unit_cost ＝ この商品に寝ている仕入資金（文字列）。 */
  tied_value: string;
  /** 原価未設定で販売価格を暫定使用した明細は true。 */
  is_estimated: boolean;
  is_manual_cost: boolean;
  /** 全期間での最終販売日時（ISO8601）。一度も売れていなければ null。 */
  last_sold_at: string | null;
  /** 最終販売からの経過日数。売れたことが無ければ null。 */
  days_since_last_sale: number | null;
}

export interface DeadStockReport {
  lines: DeadStockLine[];
  total_tied_value: string;
  /** 「無販売」を判定した窓（日）。 */
  window_days: number;
  has_estimated_lines: boolean;
}

type AnalyticsIdentityLine = {
  variant_id: StockyPublicBigIntId;
  product_id: StockyPublicBigIntId;
  shopify_variant_id: StockyPublicBigIntId;
};

function normalizeAnalyticsLine<T extends AnalyticsIdentityLine>(
  value: unknown,
): T {
  if (value === null || typeof value !== "object") {
    throw new TypeError("inventory analytics line response is invalid");
  }
  const line = value as Omit<
    T,
    "variant_id" | "product_id" | "shopify_variant_id"
  > & {
    variant_id: unknown;
    product_id: unknown;
    shopify_variant_id: unknown;
  };
  return {
    ...line,
    variant_id: canonicalStockyPublicBigIntId(line.variant_id),
    product_id: canonicalStockyPublicBigIntId(line.product_id),
    shopify_variant_id: canonicalStockyPublicBigIntId(
      line.shopify_variant_id,
    ),
  } as T;
}

function normalizeInventoryAnalytics(value: unknown): InventoryAnalytics {
  if (value === null || typeof value !== "object") {
    throw new TypeError("inventory analytics response is invalid");
  }
  const analytics = value as Omit<
    InventoryAnalytics,
    "valuation" | "abc_analysis"
  > & {
    valuation: unknown;
    abc_analysis: unknown;
  };
  if (
    analytics.valuation === null ||
    typeof analytics.valuation !== "object" ||
    analytics.abc_analysis === null ||
    typeof analytics.abc_analysis !== "object"
  ) {
    throw new TypeError("inventory analytics response is invalid");
  }
  const valuation = analytics.valuation as Omit<InventoryValuation, "lines"> & {
    lines: unknown;
  };
  const abcAnalysis = analytics.abc_analysis as Omit<AbcAnalysis, "lines"> & {
    lines: unknown;
  };
  if (!Array.isArray(valuation.lines) || !Array.isArray(abcAnalysis.lines)) {
    throw new TypeError("inventory analytics lines response is invalid");
  }
  return {
    ...analytics,
    valuation: {
      ...valuation,
      lines: valuation.lines.map((line) =>
        normalizeAnalyticsLine<ValuationLine>(line),
      ),
    },
    abc_analysis: {
      ...abcAnalysis,
      lines: abcAnalysis.lines.map((line) =>
        normalizeAnalyticsLine<AbcLine>(line),
      ),
    },
  };
}

function normalizeDeadStockReport(value: unknown): DeadStockReport {
  if (value === null || typeof value !== "object") {
    throw new TypeError("dead stock response is invalid");
  }
  const report = value as Omit<DeadStockReport, "lines"> & { lines: unknown };
  if (!Array.isArray(report.lines)) {
    throw new TypeError("dead stock lines response is invalid");
  }
  return {
    ...report,
    lines: report.lines.map((line) =>
      normalizeAnalyticsLine<DeadStockLine>(line),
    ),
  };
}

/**
 * 在庫評価額＋ABC分析を取得する（Pro 看板機能）。
 *
 * `locationId` は拠点別の在庫評価額用（Business 限定）。null=未選択（バックエンドが主拠点へ解決）。
 * バックエンドは非Proに 403 を返すため、呼び出し側はプランで事前ゲートしてから
 * 呼ぶ（`enabled` で query をスキップ）。
 */
export function getInventoryAnalytics(
  locationId: StockyPublicBigIntId | null = null,
): Promise<InventoryAnalytics> {
  const query =
    locationId !== null
      ? { location_id: canonicalStockyPublicBigIntId(locationId) }
      : undefined;
  return apiFetch<unknown>("/api/inventory-analytics", { query }).then(
    normalizeInventoryAnalytics,
  );
}

/**
 * デッドストック（滞留在庫）一覧を取得する（Pro 看板機能）。
 *
 * `locationId` は拠点別の在庫用（Business 限定）。null=未選択（バックエンドが主拠点へ解決）。
 * バックエンドは非Proに 403 を返すため、呼び出し側はプランで事前ゲートしてから呼ぶ。
 */
export function getDeadStock(
  locationId: StockyPublicBigIntId | null = null,
): Promise<DeadStockReport> {
  const query =
    locationId !== null
      ? { location_id: canonicalStockyPublicBigIntId(locationId) }
      : undefined;
  return apiFetch<unknown>("/api/inventory-analytics/dead-stock", {
    query,
  }).then(normalizeDeadStockReport);
}
